// src/views/components/PromotionSection/PromotionSection.jsx
import React, { useState, useEffect } from 'react';
import { Play, X, Sparkles, Film, ArrowRight } from 'lucide-react';
import { supabase } from '../../../controllers/services/supabase';
import './PromotionSection.css';

const extractYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/i);
  return match ? match[1] : null;
};

const DEFAULT_PROMOTIONS = [
  {
    id: 'default-p1',
    title: 'Zero Brokerage Advantage: Direct Owner Connection',
    description: 'Learn how thousands of families in Ghazipur, Varanasi & Lucknow saved lakhs on brokerage fees with verified direct listings.',
    source_type: 'youtube',
    video_url: 'https://www.youtube.com/watch?v=ScMzIvxBSi4',
    thumbnail_url: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1200&q=80',
    priority: 1
  },
  {
    id: 'default-p2',
    title: 'Turnkey Construction & Modern Architectural Elevations',
    description: 'From 3D house elevation and soil testing to RCC structure and interior handover with timely progress guarantee.',
    source_type: 'youtube',
    video_url: 'https://www.youtube.com/watch?v=ScMzIvxBSi4',
    thumbnail_url: 'https://images.unsplash.com/photo-1541888946425-d0fbb186c5f7?auto=format&fit=crop&w=1200&q=80',
    priority: 2
  }
];

const PromotionSection = ({ position = 'Promotion Section' }) => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeModalVideo, setActiveModalVideo] = useState(null);

  useEffect(() => {
    const fetchPromotions = async () => {
      try {
        const { data, error } = await supabase
          .from('promotion_videos')
          .select('*')
          .eq('status', 'Active')
          .in('show_on', ['Website', 'Both'])
          .order('priority', { ascending: true });

        if (error || !data || data.length === 0) {
          // Check localStorage fallback from admin
          const rawFallback = localStorage.getItem('nobrokerage_promotion_videos_fallback');
          if (rawFallback) {
            const parsed = JSON.parse(rawFallback).filter(
              (p) => p.status === 'Active' && ['Website', 'Both'].includes(p.show_on)
            );
            setPromotions(parsed.length > 0 ? parsed : DEFAULT_PROMOTIONS);
          } else {
            setPromotions(DEFAULT_PROMOTIONS);
          }
        } else {
          setPromotions(data);
        }
      } catch (err) {
        console.warn('Using default promotions:', err);
        setPromotions(DEFAULT_PROMOTIONS);
      } finally {
        setLoading(false);
      }
    };

    fetchPromotions();
  }, [position]);

  if (loading || promotions.length === 0) return null;

  return (
    <section className="promotion-section">
      <div className="container">
        {/* Section Header */}
        <div className="promotion-header text-center">
          <div className="promotion-badge">
            <Sparkles size={14} className="promotion-badge-icon" />
            <span>BRAND SPOTLIGHT</span>
          </div>
          <h2 className="promotion-title">See How No Brokerage Works</h2>
          <p className="promotion-subtitle">
            Watch our latest walkthroughs, brand insights, and construction milestone videos.
          </p>
        </div>

        {/* Video Grid */}
        <div className="promotion-grid">
          {promotions.slice(0, 3).map((promo, idx) => {
            const ytId = extractYouTubeId(promo.video_url);
            const thumb = promo.thumbnail_url || (ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : '');

            return (
              <div key={promo.id || idx} className="promotion-card">
                {/* Video Thumbnail Wrapper */}
                <div
                  className="promotion-media-wrap"
                  onClick={() => setActiveModalVideo(promo)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Play ${promo.title}`}
                  onKeyDown={(e) => e.key === 'Enter' && setActiveModalVideo(promo)}
                >
                  <img
                    src={thumb}
                    alt={promo.title}
                    className="promotion-thumb"
                    loading="lazy"
                  />
                  <div className="promotion-media-overlay">
                    <div className="promotion-play-btn">
                      <Play size={20} className="promotion-play-icon" />
                    </div>
                  </div>
                  <span className="promotion-duration-badge">
                    <Film size={12} />
                    <span>Watch Video</span>
                  </span>
                </div>

                {/* Card Content */}
                <div className="promotion-content">
                  <h3 className="promotion-card-title">{promo.title}</h3>
                  {promo.description && (
                    <p className="promotion-card-desc">{promo.description}</p>
                  )}
                  <div className="promotion-card-footer">
                    <button
                      type="button"
                      className="promotion-watch-link"
                      onClick={() => setActiveModalVideo(promo)}
                    >
                      <span>Watch in Fullscreen</span>
                      <ArrowRight size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Fullscreen Video Modal */}
      {activeModalVideo && (
        <div
          className="promotion-modal-backdrop"
          onClick={() => setActiveModalVideo(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="promotion-modal-box"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="promotion-modal-header">
              <h3 className="promotion-modal-title">{activeModalVideo.title}</h3>
              <button
                type="button"
                className="promotion-modal-close"
                onClick={() => setActiveModalVideo(null)}
                aria-label="Close video player"
              >
                <X size={20} />
              </button>
            </div>

            {/* Video Player Frame */}
            <div className="promotion-player-wrapper">
              {activeModalVideo.source_type === 'youtube' || extractYouTubeId(activeModalVideo.video_url) ? (
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${extractYouTubeId(activeModalVideo.video_url)}?autoplay=1&rel=0`}
                  title={activeModalVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="promotion-iframe"
                />
              ) : activeModalVideo.video_url?.includes('vimeo.com') ? (
                <iframe
                  src={`https://player.vimeo.com/video/${activeModalVideo.video_url.match(/vimeo\.com\/(\d+)/)?.[1]}?autoplay=1`}
                  title={activeModalVideo.title}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  className="promotion-iframe"
                />
              ) : (
                <video
                  src={activeModalVideo.video_url}
                  controls
                  autoPlay
                  className="promotion-video-elem"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PromotionSection;
